import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Ban, BadgeCheck, Loader2, ArrowLeft } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { LOGO_URL } from "@/lib/assets";

export default function WithdrawSelf() {
  const [params] = useSearchParams();
  const eventId = params.get("event_id") || "";
  const [email, setEmail] = useState("");
  const [nom, setNom] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!eventId) setError("This link is missing the meeting. Please use the Withdraw button on the club website.");
  }, [eventId]);

  const lookup = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setNom(null);
    try {
      const { data } = await api.get("/nominations/lookup", { params: { event_id: eventId, email } });
      setNom(data);
      if (data.withdrawn) setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "We couldn't find a nomination for that email at this meeting");
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async () => {
    setWithdrawing(true);
    try {
      const { data } = await api.post("/nominations/lookup/withdraw", { event_id: eventId, email });
      setNom(data);
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setWithdrawing(false);
    }
  };

  return (
    <main data-testid="withdraw-self-page" className="min-h-screen flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md border border-clay-border bg-clay-card p-8 sm:p-10">
        <img src={LOGO_URL} alt="Mid Western Speedway Darlington" className="h-16 mb-6" />
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-clay-orange mb-2">Mid Western Speedway Darlington</p>

        {error && !nom ? (
          <div data-testid="withdraw-self-error">
            <h1 className="font-display font-bold uppercase text-2xl tracking-tight mb-3">Nomination not found</h1>
            <p className="text-clay-muted text-sm leading-relaxed mb-6">{error}</p>
            <Link to="/" className="inline-flex items-center gap-2 text-clay-gold text-sm uppercase tracking-wider font-display font-bold">
              <ArrowLeft className="w-4 h-4" /> Back to the club site
            </Link>
          </div>
        ) : done ? (
          <div data-testid="withdraw-self-done" className="text-center py-6">
            <BadgeCheck className="w-14 h-14 text-clay-gold mx-auto mb-4" />
            <h1 className="font-display font-bold uppercase text-2xl tracking-tight mb-3">Nomination withdrawn</h1>
            <p className="text-clay-muted text-sm leading-relaxed">
              All done{nom ? `, ${nom.driver_name?.split(" ")[0]}` : ""} — you're withdrawn from {nom?.event_title || "the meeting"}.
              The club has been notified. Changed your mind? Just reply to your nomination email and we'll reinstate you.
            </p>
          </div>
        ) : nom ? (
          <>
            <h1 className="font-display font-bold uppercase text-2xl tracking-tight mb-1">Withdraw Nomination?</h1>
            <p data-testid="withdraw-self-details" className="text-clay-muted text-sm mb-6">
              {nom.driver_name} — Car #{nom.car_number || "—"} ({nom.racing_class || "class TBC"}) at {nom.event_title}, {nom.event_date}.
            </p>
            <p className="text-clay-muted text-sm mb-6 leading-relaxed">
              You'll stay on the nominations list crossed out, so the club can reinstate you if plans change.
            </p>
            <button
              data-testid="withdraw-self-confirm-button"
              onClick={withdraw}
              disabled={withdrawing}
              className="w-full bg-red-600 hover:bg-red-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-display font-bold uppercase tracking-wider px-6 py-4 transition-colors flex items-center justify-center gap-2"
            >
              {withdrawing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />}
              {withdrawing ? "Withdrawing…" : "Yes, Withdraw My Nomination"}
            </button>
            <button
              data-testid="withdraw-self-back-button"
              onClick={() => setNom(null)}
              className="w-full mt-3 text-clay-muted hover:text-clay-text text-xs uppercase tracking-wider font-display font-bold py-2 transition-colors"
            >
              Not you? Check a different email
            </button>
          </>
        ) : (
          <>
            <h1 className="font-display font-bold uppercase text-2xl tracking-tight mb-1">Withdraw Nomination</h1>
            <p className="text-clay-muted text-sm mb-6 leading-relaxed">
              Enter the email address you nominated with and we'll find your entry.
            </p>
            <form onSubmit={lookup} className="space-y-4">
              <input
                data-testid="withdraw-self-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full bg-clay-bg border border-clay-border px-4 py-3 text-clay-text text-sm placeholder:text-clay-muted/50 focus:outline-none focus:border-clay-orange"
              />
              <button
                data-testid="withdraw-self-find-button"
                type="submit"
                disabled={busy}
                className="w-full bg-clay-orange hover:bg-clay-gold disabled:opacity-40 disabled:cursor-not-allowed text-clay-bg font-display font-bold uppercase tracking-wider px-6 py-4 transition-colors flex items-center justify-center gap-2"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {busy ? "Finding your nomination…" : "Find My Nomination"}
              </button>
            </form>
          </>
        )}
      </div>
    </main>
  );
}
